import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
  type AuthError,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithCredential,
} from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';
import {
  AccessPermission,
  readUserMe,
  type OrganisationInDb,
  type UserInDbMe,
  type UserPermissions,
} from '@/api';
import type { OfficeGoogleAuthSuccessPayload } from '@/office/auth/types';
import { extractErrorCode } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

/**
 * The review area (reviews, playbooks, review projects) is on when any of its
 * parts is. Each flag already folds in the `contract_review` feature.
 */
export const REVIEW_AREA_PERMISSIONS: (keyof UserPermissions)[] = [
  'can_create_reviews',
  'can_update_reviews',
  'can_use_playbooks',
  'can_create_playbooks',
  'can_use_review_projects',
];

export function hasReviewArea(permissions: UserPermissions): boolean {
  return REVIEW_AREA_PERMISSIONS.some(permission => permissions[permission]);
}

/**
 * The backend's verdict on the current Firebase session, mapped from the
 * machine-readable error codes of `readUserMe`.
 *
 * - 'unknown': no validation has run (signed out / initial)
 * - 'ok': backend user present — fully authenticated
 * - 'unverified': password account whose email is not verified yet
 * - 'unprovisioned': Firebase session exists but no backend user (mid-signup)
 * - 'inactive': deactivated account
 * - 'error': invalid token, unknown account, or unreachable backend
 */
export type BackendAuthState =
  'unknown' | 'ok' | 'unverified' | 'unprovisioned' | 'inactive' | 'error';

/**
 * Resolves where a freshly validated session should land: partial-auth states
 * have dedicated pages, everything else goes to the given fallback.
 */
export function routeForBackendAuthState(state: BackendAuthState, fallback = '/'): string {
  if (state === 'unverified') return '/verify-email';
  if (state === 'unprovisioned') return '/sign-up/complete';
  return fallback;
}

let authStateUnsubscribe: (() => void) | null = null;

// Tracks whether the store has been instantiated, without instantiating it.
// The API client's auth callback (config/api.ts) checks this so that public
// pages never create the store (and thus never initialize Firebase).
let created = false;

export function isAuthStoreCreated(): boolean {
  return created;
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    authStateUnsubscribe?.();
    authStateUnsubscribe = null;
  });
}

/**
 * Custom error class for authentication-related errors
 */
class AuthStoreError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'AuthStoreError';
  }
}

/**
 * Maps Firebase auth error codes to user-friendly messages
 * @param code - Firebase auth error code
 * @returns User-friendly error message
 */
export function getAuthErrorMessage(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please try again.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled. Please try again.';
    case 'auth/redirect-cancelled-by-user':
      return 'Google sign-in was cancelled before it completed.';
    case 'auth/unauthorized-domain':
      return 'This localhost domain is not authorized in Firebase Auth. Add it to Authorized Domains.';
    case 'auth/backend-validation-failed':
      return 'Your account was not found in the system. Please contact an administrator.';
    case 'auth/account-inactive':
      return 'Your account has been deactivated. Please contact an administrator.';
    case 'auth/office-session-missing':
      return 'Google sign-in completed, but the add-in could not establish a Firebase session.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Authentication store for managing user authentication state and operations
 *
 * This store handles:
 * - User authentication state
 * - Email/password sign up and sign in
 * - Google authentication
 * - User session management
 * - Demo mode detection
 */
export const useAuthStore = defineStore('auth', () => {
  created = true;
  const auth = getFirebaseAuth();

  /** Current authenticated user or null if not authenticated */
  const user = ref<User | null>(null);

  /** Current backend user data or null if not authenticated in backend */
  const backendUser = ref<UserInDbMe | null>(null);

  /** Loading state for async operations */
  const loading = ref(false);

  /** Current error message or null if no error */
  const error = ref<string | null>(null);

  /**
   * Indicates whether the auth state has been initialized
   * This prevents race conditions during initial auth state check
   */
  const isReady = ref(false);

  const isHandlingAuthChange = ref(false);

  /** The backend's verdict on the current Firebase session */
  const backendAuthState = ref<BackendAuthState>('unknown');

  const clearSession = (): void => {
    user.value = null;
    backendUser.value = null;
  };

  const signOutWithAuthChangeGuard = async (): Promise<void> => {
    const wasHandlingAuthChange = isHandlingAuthChange.value;
    isHandlingAuthChange.value = true;
    try {
      await signOut(auth);
    } finally {
      isHandlingAuthChange.value = wasHandlingAuthChange;
    }
  };

  /**
   * Validates user with backend after Firebase authentication
   *
   * Branches on the backend's machine-readable error codes: the partial-auth
   * states ('unverified' / 'unprovisioned') keep the Firebase session — the
   * user needs it to verify their email or complete signup, and the router
   * redirects them there. Only genuinely dead sessions clear it.
   * @param firebaseUser - Firebase authenticated user
   * @returns The resulting backend auth state
   */
  const validateWithBackend = async (firebaseUser: User): Promise<BackendAuthState> => {
    try {
      // Set the user first so the token can be retrieved by API calls
      user.value = firebaseUser;

      const { data, error: apiError, response } = await readUserMe({ throwOnError: false });
      if (data) {
        backendUser.value = data;
        backendAuthState.value = 'ok';
        return 'ok';
      }

      const code = extractErrorCode(apiError);
      if (code === 'email_unverified' || code === 'user_not_provisioned') {
        backendUser.value = null;
        backendAuthState.value = code === 'email_unverified' ? 'unverified' : 'unprovisioned';
        return backendAuthState.value;
      }

      logger.error('Backend user validation failed:', apiError);
      // No HTTP response means the backend was never reached — not that the
      // account is missing, so don't tell the user to contact an admin.
      if (!response) {
        clearSession();
        backendAuthState.value = 'error';
        error.value = getAuthErrorMessage('auth/network-request-failed');
        return 'error';
      }
      if (code === 'user_inactive') {
        clearSession();
        backendAuthState.value = 'inactive';
        error.value = getAuthErrorMessage('auth/account-inactive');
        return 'inactive';
      }
    } catch (error_) {
      logger.error('Backend user validation failed:', error_);
    }

    clearSession();
    backendAuthState.value = 'error';
    error.value = 'Your account was not found in the system. Please contact an administrator.';
    return 'error';
  };

  /**
   * Re-validates the current session after the user reports having verified
   * their email. The forced token refresh is load-bearing: Firebase mints
   * `email_verified` into the ID token at issue time, so the cached token is
   * stale right after verification.
   * @returns The resulting backend auth state
   */
  const refreshBackendValidation = async (): Promise<BackendAuthState> => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      backendAuthState.value = 'unknown';
      return 'unknown';
    }
    await firebaseUser.reload();
    await firebaseUser.getIdToken(true);
    return await validateWithBackend(firebaseUser);
  };

  // Initialize auth state observer
  authStateUnsubscribe?.();
  authStateUnsubscribe = onAuthStateChanged(auth, firebaseUser => {
    void (async (): Promise<void> => {
      if (isHandlingAuthChange.value) {
        if (!firebaseUser) {
          clearSession();
        }
        isReady.value = true;
        return;
      }

      isHandlingAuthChange.value = true;
      try {
        if (firebaseUser) {
          const state = await validateWithBackend(firebaseUser);
          if (state === 'inactive' || state === 'error') {
            // Only genuinely dead sessions sign out of Firebase; the partial
            // states keep the session so the user can verify / finish signup.
            await signOutWithAuthChangeGuard();
          }
        } else {
          clearSession();
          backendAuthState.value = 'unknown';
        }
      } finally {
        isReady.value = true;
        isHandlingAuthChange.value = false;
      }
    })();
  });

  /**
   * Signs in an existing user with email and password
   * @param email - User's email address
   * @param password - User's password
   * @throws AuthStoreError if signin fails
   */
  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      loading.value = true;
      error.value = null;

      const wasHandlingAuthChange = isHandlingAuthChange.value;
      isHandlingAuthChange.value = true;
      try {
        // First authenticate with Firebase
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const firebaseUser = userCredential.user;

        // Then validate with backend
        const state = await validateWithBackend(firebaseUser);
        if (state === 'inactive' || state === 'error') {
          await signOutWithAuthChangeGuard();
          throw new AuthStoreError(
            'Backend validation failed',
            state === 'inactive' ? 'auth/account-inactive' : 'auth/backend-validation-failed'
          );
        }
      } finally {
        isHandlingAuthChange.value = wasHandlingAuthChange;
      }
    } catch (error_: unknown) {
      // Handle Firebase authentication errors
      if (error_ instanceof AuthStoreError) {
        // Re-throw our custom error
        throw error_;
      }

      const authError = error_ as AuthError;
      const errorMessage = getAuthErrorMessage(authError.code);
      error.value = errorMessage;
      throw new AuthStoreError(errorMessage, authError.code);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Signs in a user with Google authentication
   * @throws AuthStoreError if signin fails
   */
  const signInWithGoogle = async (): Promise<void> => {
    try {
      loading.value = true;
      error.value = null;

      const wasHandlingAuthChange = isHandlingAuthChange.value;
      isHandlingAuthChange.value = true;
      try {
        // Authenticate with Firebase using Google
        const provider = new GoogleAuthProvider();
        const userCredential = await signInWithPopup(auth, provider);
        const firebaseUser = userCredential.user;

        // Then validate with backend
        const state = await validateWithBackend(firebaseUser);
        if (state === 'inactive' || state === 'error') {
          await signOutWithAuthChangeGuard();
          throw new AuthStoreError(
            'Backend validation failed',
            state === 'inactive' ? 'auth/account-inactive' : 'auth/backend-validation-failed'
          );
        }
      } finally {
        isHandlingAuthChange.value = wasHandlingAuthChange;
      }
    } catch (error_: unknown) {
      // Handle Firebase authentication errors
      if (error_ instanceof AuthStoreError) {
        // Re-throw our custom error
        throw error_;
      }

      const authError = error_ as AuthError;
      const errorMessage = getAuthErrorMessage(authError.code);
      error.value = errorMessage;
      throw new AuthStoreError(errorMessage, authError.code);
    } finally {
      loading.value = false;
    }
  };

  async function waitForCurrentFirebaseUser(timeoutMs = 6000): Promise<User | null> {
    if (auth.currentUser) {
      return auth.currentUser;
    }

    return await new Promise(resolve => {
      const timer = window.setTimeout(() => {
        unsubscribe();
        resolve(auth.currentUser);
      }, timeoutMs);

      const unsubscribe = onAuthStateChanged(auth, firebaseUser => {
        if (!firebaseUser) {
          return;
        }

        window.clearTimeout(timer);
        unsubscribe();
        resolve(firebaseUser);
      });
    });
  }

  /**
   * Completes Google authentication after an Office dialog login.
   *
   * The dialog runs in a separate Office webview, so the task pane signs in with
   * the returned Google credential to establish its own Firebase user session.
   */
  const signInWithGoogleOfficePayload = async (
    payload: OfficeGoogleAuthSuccessPayload
  ): Promise<void> => {
    try {
      loading.value = true;
      error.value = null;

      const wasHandlingAuthChange = isHandlingAuthChange.value;
      isHandlingAuthChange.value = true;
      try {
        let firebaseUser: User | null = null;
        const credential = payload.credential;
        if (credential?.idToken || credential?.accessToken) {
          const firebaseCredential = GoogleAuthProvider.credential(
            credential.idToken ?? null,
            credential.accessToken ?? null
          );
          const userCredential = await signInWithCredential(auth, firebaseCredential);
          firebaseUser = userCredential.user;
        } else {
          firebaseUser = await waitForCurrentFirebaseUser();
        }

        if (!firebaseUser) {
          throw new AuthStoreError(
            'Google sign-in completed, but the add-in could not establish a Firebase session.',
            'auth/office-session-missing'
          );
        }

        const state = await validateWithBackend(firebaseUser);
        if (state === 'inactive' || state === 'error') {
          await signOutWithAuthChangeGuard();
          throw new AuthStoreError(
            'Backend validation failed',
            state === 'inactive' ? 'auth/account-inactive' : 'auth/backend-validation-failed'
          );
        }
      } finally {
        isHandlingAuthChange.value = wasHandlingAuthChange;
      }
    } catch (error_: unknown) {
      if (error_ instanceof AuthStoreError) {
        error.value = getAuthErrorMessage(error_.code);
        throw error_;
      }

      const authError = error_ as AuthError;
      const errorMessage = getAuthErrorMessage(authError.code);
      error.value = errorMessage;
      throw new AuthStoreError(errorMessage, authError.code);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Signs out the current user
   * @throws AuthStoreError if logout fails
   */
  const logout = async (): Promise<void> => {
    try {
      loading.value = true;
      error.value = null;
      await signOutWithAuthChangeGuard();
      clearSession();
      backendAuthState.value = 'unknown';
    } catch (error_: unknown) {
      const authError = error_ as AuthError;
      const errorMessage = getAuthErrorMessage(authError.code);
      error.value = errorMessage;
      throw new AuthStoreError(errorMessage, authError.code);
    } finally {
      loading.value = false;
    }
  };

  /** Whether a user is currently authenticated */
  const isAuthenticated = computed((): boolean => {
    return user.value !== null && backendUser.value !== null;
  });

  const sessionUser = computed((): User | null => {
    return user.value;
  });

  const sessionEmail = computed((): string | null => {
    return user.value?.email ?? backendUser.value?.email ?? null;
  });

  const getAccessToken = async (): Promise<string | null> => {
    const firebaseUser = user.value ?? auth.currentUser;
    return firebaseUser ? await firebaseUser.getIdToken() : null;
  };

  const EMPTY_PERMISSIONS: UserPermissions = {
    can_create_reviews: false,
    can_update_reviews: false,
    can_create_playbooks: false,
    can_use_playbooks: false,
    can_use_signing: false,
    all_contractual_parties_access: AccessPermission._0,
    all_contracts_access: AccessPermission._0,
    is_org_admin: false,
    is_platform_admin: false,
    can_use_review_projects: false,
    can_use_contract_management: false,
    can_use_contract_generation: false,
    can_create_organisation: false,
  };

  /**
   * The user's effective permissions in their active organisation.
   *
   * The backend already OR-merges role capabilities, elevates admins (org /
   * platform) to every capability and ANDs in the organisation feature that
   * gates it — so consumers read these attributes directly (e.g.
   * `userPermissions.can_use_signing`) and never consult `organisation.features`.
   */
  const userPermissions = computed<UserPermissions>(() => {
    return backendUser.value?.permissions ?? EMPTY_PERMISSIONS;
  });

  /**
   * Whether the user is inside an organisation. A user can exist without one
   * (invited nowhere yet, or removed from their last org); the backend then
   * returns `active_organisation: null` and every org-scoped endpoint fails,
   * so the app shows the org-less home and skips those calls.
   */
  const hasActiveOrganisation = computed<boolean>(() => {
    return backendUser.value?.active_organisation != null;
  });

  const canUseReviewArea = computed<boolean>(() => hasReviewArea(userPermissions.value));

  /** The organisations the user can switch into (empty for an org-less user). */
  const organisations = computed<OrganisationInDb[]>(() => {
    return backendUser.value?.organisations ?? [];
  });

  return {
    user,
    sessionUser,
    sessionEmail,
    backendUser,
    loading,
    error,
    isReady,
    backendAuthState,
    getAccessToken,
    signIn,
    signInWithGoogle,
    signInWithGoogleOfficePayload,
    logout,
    isAuthenticated,
    validateWithBackend,
    refreshBackendValidation,
    userPermissions,
    canUseReviewArea,
    hasActiveOrganisation,
    organisations,
  };
});
